/**
 * DriftDetailDrawer Component
 *
 * Right-sliding detail panel showing full configuration drift details, AI
 * analysis, risk score, audit history, and action controls. Restyled to the
 * editorial palette to match the queue table (paper / navy / cream + sand-deep
 * / blue-deep / green-deep / red-deep semantic accents). Reuses the shared
 * `<SevPill>`, `<StatusPill>`, `<NoticeRail>`, `<OutlineButton>`, and
 * `<PrimaryButton>` primitives from the queue and shell so future palette
 * tweaks land in one place.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { api } from '@/lib/api'
import apiClient from '@/lib/api'
import { DriftStatus } from '@/types'
import { SevPill, StatusPill } from '@/components/Drift/DriftTableWithBulkActions'
import {
  NoticeRail,
  OutlineButton,
  PrimaryButton,
} from '@/components/BaselineCompliance/DriftManagementTab'
import BulkActionCommentModal from '@/components/Drift/BulkActionCommentModal'

interface DriftDetailDrawerProps {
  driftId: string | null
  isOpen: boolean
  onClose: () => void
  onStatusUpdate: (id: string, status: string, comment?: string) => void
  onApprove: (id: string, comment?: string) => void
  onReject: (id: string, comment?: string) => void
  isUpdating: boolean
}

// Audit entry shape mirrors api.getDriftAuditHistory's return element. Keeping
// it local because the API helper returns an inline structural type (no
// exported alias) — when that gets promoted, swap this for the shared one.
type AuditEntry = Awaited<ReturnType<typeof api.getDriftAuditHistory>>[number]

// Approval-impact response is fetched via raw apiClient (no shared type). The
// fields used in the drawer are listed here so the impact block stays readable
// without `any`s scattered through the markup.
interface ImpactedDrift {
  id: string
  setting: string
  severity: import('@/types').DriftSeverity
}
interface ApprovalImpact {
  willTriggerResourceLevelUpdate?: boolean
  reason?: string
  recommendation?: string
  impactedDrifts?: ImpactedDrift[]
}

/* ────────────────────────────────────────────────────────────────────
   Audit event tone mapping. Editorial palette: green-deep / red-deep /
   blue-deep / sand-deep / mute. Mirrors the queue's severity treatment.
   ──────────────────────────────────────────────────────────────────── */

type AuditTone = 'approve' | 'reject' | 'auto' | 'remediate' | 'evolve' | 'neutral'

const AUDIT_TONE: Record<string, AuditTone> = {
  Approved: 'approve',
  Rejected: 'reject',
  AutoApproved: 'auto',
  Remediated: 'remediate',
  ValuesEvolved: 'evolve',
  StatusChanged: 'neutral',
  Redetected: 'neutral',
}

const AUDIT_TONE_BG: Record<AuditTone, string> = {
  approve: 'rgba(118,214,157,0.16)',
  reject: 'rgba(229,115,115,0.12)',
  auto: 'rgba(167,211,240,0.22)',
  remediate: 'rgba(118,214,157,0.16)',
  evolve: 'rgba(235,212,180,0.22)',
  neutral: 'var(--color-paper-warm, #FBF8F2)',
}

const AUDIT_TONE_FG: Record<AuditTone, string> = {
  approve: 'var(--color-green-deep, #4A9B6E)',
  reject: 'var(--color-red-deep, #C84B4B)',
  auto: 'var(--color-blue-deep, #7BA8C4)',
  remediate: 'var(--color-green-deep, #4A9B6E)',
  evolve: 'var(--color-sand-deep, #C9AD80)',
  neutral: 'var(--color-mute, #6B7280)',
}

const AUDIT_LABEL: Record<string, string> = {
  Approved: 'Approved',
  Rejected: 'Rejected',
  AutoApproved: 'Auto-approved',
  Remediated: 'Remediated',
  ValuesEvolved: 'Values evolved',
  StatusChanged: 'Status changed',
  Redetected: 'Redetected',
}

const REMEDIATION_LABEL: Record<string, string> = {
  ApprovedBaseline: 'Approved baseline',
  RejectedManualFix: 'Manual fix',
  AutoCorrected: 'Auto-corrected',
  TenantSelfCorrected: 'Tenant self-corrected',
  BaselineChangedExternally: 'Baseline changed externally',
}

/* ────────────────────────────────────────────────────────────────────
   Risk score → tier mapping. Lines up with the four severity buckets
   in the queue so the visual reads consistently.
   ──────────────────────────────────────────────────────────────────── */

type RiskTier = 0 | 1 | 2 | 3 // low / med / high / crit

function riskTier(score: number): RiskTier {
  if (score >= 0.8) return 3
  if (score >= 0.6) return 2
  if (score >= 0.4) return 1
  return 0
}

const RISK_TIER_LABEL: Record<RiskTier, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical',
}

const RISK_TIER_COLOR: Record<RiskTier, string> = {
  0: 'var(--color-green-deep, #4A9B6E)',
  1: 'var(--color-blue-deep, #7BA8C4)',
  2: 'var(--color-sand-deep, #C9AD80)',
  3: 'var(--color-red-deep, #C84B4B)',
}

export default function DriftDetailDrawer({
  driftId,
  isOpen,
  onClose,
  onStatusUpdate,
  onApprove,
  onReject,
  isUpdating,
}: DriftDetailDrawerProps) {
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'status' | null>(null)
  const [showImpactWarning, setShowImpactWarning] = useState(false)

  const {
    data: drift,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['configuration-drift', driftId],
    queryFn: () => (driftId ? api.getConfigurationDrift(driftId) : null),
    enabled: !!driftId,
  })

  // Check approval impact for complex property additions
  const { data: approvalImpact } = useQuery({
    queryKey: ['approval-impact', driftId],
    queryFn: async () => {
      if (!driftId) return null
      try {
        const response = await apiClient.get(`/configurationdrift/${driftId}/approval-impact`)
        return response.data
      } catch (error) {
        console.error('Failed to check approval impact:', error)
        return null
      }
    },
    enabled: !!driftId && drift?.status === 'New',
  })

  // Query audit history for this drift
  const {
    data: auditHistory,
    isLoading: isAuditLoading,
  } = useQuery({
    queryKey: ['drift-audit-history', driftId],
    queryFn: () => (driftId ? api.getDriftAuditHistory(driftId) : null),
    enabled: !!driftId,
  })

  const handleAction = (type: 'approve' | 'reject') => {
    // Approvals that would cascade into resource-level updates show the impact
    // notice first; the user then either backs out or proceeds to the comment
    // modal via "Approve anyway".
    if (type === 'approve' && approvalImpact?.willTriggerResourceLevelUpdate && !showImpactWarning) {
      setShowImpactWarning(true)
      setActionType(type)
      return
    }

    setActionType(type)
    setShowCommentModal(true)
  }

  const handleStatusChange = (status: string) => {
    if (!driftId) return
    onStatusUpdate(driftId, status)
  }

  const handleCommentConfirm = (comment: string) => {
    if (!actionType || !driftId) return

    const trimmed = comment.trim() || undefined
    if (actionType === 'approve') {
      onApprove(driftId, trimmed)
    } else if (actionType === 'reject') {
      onReject(driftId, trimmed)
    }

    setShowCommentModal(false)
    setActionType(null)
    setShowImpactWarning(false)
  }

  const closeCommentModal = () => {
    setShowCommentModal(false)
    setActionType(null)
  }

  const canApprove = drift?.status === 'PendingApproval' || drift?.status === 'Pending' || drift?.status === 'New'
  const canReject = drift?.status !== 'Rejected' && drift?.status !== 'Remediated'

  return (
    <>
      {/* Backdrop — softer than the legacy black/60. Navy at 30% lets the cream
          page underneath read through and avoids the cold-tool stark cover. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-[color:var(--color-navy,#212938)]/30 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div
          className="flex h-full flex-col border-l bg-[color:var(--color-paper,#fff)] shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.18)]"
          style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
        >
          {/* Sticky header — eyebrow + close. The detail's italic-mixed
              headline lives one section down in the drift identity block so
              the header stays a quiet rail, not a competing title. */}
          <div
            className="sticky top-0 z-10 flex items-center justify-between border-b bg-[color:var(--color-paper,#fff)]/95 px-7 py-4 backdrop-blur-sm"
            style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
          >
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
                Drift · detail
              </div>
              <div className="mt-0.5 text-[11px] font-semibold text-[color:var(--color-faint,#9CA3AF)]">
                By Guardian
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 rounded-full p-2 text-[color:var(--color-mute,#6B7280)] transition-colors hover:bg-[color:var(--color-paper-warm,#FBF8F2)] hover:text-[color:var(--color-navy,#212938)]"
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-7 overflow-y-auto px-7 py-6">
            {isLoading ? (
              <DrawerSkeleton />
            ) : error || !drift ? (
              <div className="py-12 text-center text-sm text-[color:var(--color-red-deep,#C84B4B)]">
                Couldn&apos;t load this drift. Try again in a moment.
              </div>
            ) : (
              <>
                {/* ============================================================
                    Drift identity — sev pill + detected timestamp, then the
                    italic-mixed headline carrying component · setting.
                    ============================================================ */}
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <SevPill severity={drift.severity} />
                    <span
                      className="font-mono text-[11px] font-semibold tabular-nums"
                      style={{ color: 'var(--color-faint, #9CA3AF)' }}
                    >
                      {format(new Date(drift.detectedAt), 'MMM dd, yyyy · HH:mm')}
                    </span>
                  </div>
                  <h3
                    className="mt-3 font-display text-[22px] font-extrabold leading-[1.2] tracking-[-0.018em]"
                    style={{ color: 'var(--color-navy, #212938)' }}
                  >
                    {drift.component}{' '}
                    <em
                      className="font-serif font-semibold"
                      style={{
                        fontStyle: 'italic',
                        color: 'var(--color-sand-deep, #C9AD80)',
                        fontFamily: 'var(--font-serif, "Fraunces", "Inter", serif)',
                      }}
                    >
                      · {drift.setting}
                    </em>
                  </h3>
                  {drift.componentName && drift.componentName !== drift.component && (
                    <div className="mt-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
                        Resource
                      </div>
                      <div
                        className="mt-1 text-sm font-semibold"
                        style={{ color: 'var(--color-navy, #212938)' }}
                      >
                        {drift.componentName}
                      </div>
                    </div>
                  )}
                </div>

                <SectionRule />

                {/* ============================================================
                    Section · 01 — Configuration change
                    ============================================================ */}
                <section>
                  <DrawerSectionHeader
                    eyebrow="Section · 01 · The change"
                    headline={
                      <>
                        What <em>changed.</em>
                      </>
                    }
                    emColor="var(--color-sand-deep, #C9AD80)"
                  />
                  <div className="mt-4 space-y-3">
                    <ValueBlock label="Previous" tone="red" value={drift.oldValue} />
                    <ValueBlock label="Now" tone="green" value={drift.newValue} />
                  </div>
                </section>

                {/* ============================================================
                    Section · 02 — Guardian's read (description + recommended)
                    ============================================================ */}
                {(drift.humanReadableDescription || drift.recommendedAction) && (
                  <>
                    <SectionRule />
                    <section>
                      <DrawerSectionHeader
                        eyebrow="Section · 02 · Guardian's read"
                        headline={
                          <>
                            Reading the <em>signal.</em>
                          </>
                        }
                        emColor="var(--color-blue-deep, #7BA8C4)"
                        byline="By Guardian"
                      />
                      <div className="mt-4 space-y-4">
                        {drift.humanReadableDescription && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
                              What happened
                            </div>
                            <p
                              className="mt-1.5 text-sm leading-[1.6]"
                              style={{ color: 'var(--color-navy, #212938)' }}
                            >
                              {drift.humanReadableDescription}
                            </p>
                          </div>
                        )}
                        {drift.recommendedAction && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
                              What to do
                            </div>
                            <p
                              className="mt-1.5 text-sm leading-[1.6]"
                              style={{ color: 'var(--color-navy, #212938)' }}
                            >
                              {drift.recommendedAction}
                            </p>
                          </div>
                        )}
                      </div>
                    </section>
                  </>
                )}

                {/* ============================================================
                    Risk score — 4-segment editorial capsule
                    ============================================================ */}
                {typeof drift.riskScore === 'number' && (
                  <>
                    <SectionRule />
                    <section>
                      <RiskCapsule score={drift.riskScore} />
                    </section>
                  </>
                )}

                {/* ============================================================
                    Section · 03 — Audit trail
                    ============================================================ */}
                {auditHistory && auditHistory.length > 0 && (
                  <>
                    <SectionRule />
                    <section>
                      <DrawerSectionHeader
                        eyebrow="Section · 03 · Audit trail"
                        headline={
                          <>
                            How we <em>got here.</em>
                          </>
                        }
                        emColor="var(--color-blue-deep, #7BA8C4)"
                      />
                      <div className="mt-4">
                        {isAuditLoading ? (
                          <div className="animate-pulse space-y-2">
                            <div className="h-4 w-3/4 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
                            <div className="h-3 w-1/2 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
                          </div>
                        ) : (
                          <ol className="space-y-0">
                            {auditHistory.map((entry, idx) => (
                              <AuditRow
                                key={entry.id}
                                entry={entry}
                                isLast={idx === auditHistory.length - 1}
                              />
                            ))}
                          </ol>
                        )}
                      </div>
                    </section>
                  </>
                )}

                {/* ============================================================
                    Status update (kept for ops who need to nudge state without
                    going through approve/reject) — quiet paper-warm field
                    ============================================================ */}
                <SectionRule />
                <section>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
                    Status
                  </div>
                  <select
                    value={drift.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isUpdating}
                    className="mt-2 w-full rounded-[8px] border bg-[color:var(--color-paper,#fff)] px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--color-navy,#212938)]/20 disabled:opacity-50"
                    style={{
                      borderColor: 'var(--color-rule, #E6E1D5)',
                      color: 'var(--color-navy, #212938)',
                    }}
                  >
                    <option value={DriftStatus.New}>New</option>
                    <option value={DriftStatus.Pending}>Pending</option>
                    <option value={DriftStatus.PendingApproval}>Pending Approval</option>
                    <option value={DriftStatus.Approved}>Approved</option>
                    <option value={DriftStatus.AwaitingRemediation}>Awaiting Remediation</option>
                    <option value={DriftStatus.Rejected}>Rejected</option>
                    <option value={DriftStatus.Remediated}>Remediated</option>
                    <option value={DriftStatus.Ignored}>Ignored</option>
                  </select>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
                      Now
                    </span>
                    <StatusPill status={drift.status} />
                  </div>
                </section>

                {/* ============================================================
                    Impact warning — editorial NoticeRail (sand-deep tone)
                    ============================================================ */}
                {showImpactWarning && approvalImpact?.willTriggerResourceLevelUpdate && (
                  <NoticeRail
                    tone="default"
                    eyebrow="Heads up"
                    className="!mt-0"
                    headline={
                      <>
                        Bigger <em>than it looks.</em>
                      </>
                    }
                    body={
                      <ImpactBody approvalImpact={approvalImpact} />
                    }
                    actions={
                      <div className="flex flex-wrap gap-2">
                        <PrimaryButton
                          onClick={() => {
                            setShowImpactWarning(false)
                            setActionType('approve')
                            setShowCommentModal(true)
                          }}
                          disabled={isUpdating}
                        >
                          Approve anyway
                        </PrimaryButton>
                        <OutlineButton
                          onClick={() => {
                            setShowImpactWarning(false)
                            setActionType(null)
                          }}
                        >
                          Cancel
                        </OutlineButton>
                      </div>
                    }
                  />
                )}

                {/* ============================================================
                    Approve / Reject CTAs — sand-deep filled + navy outline.
                    Reuses the shell primitives so future tweaks land in one
                    place.
                    ============================================================ */}
                {!showImpactWarning && (
                  <section>
                    <div className="flex flex-wrap items-center gap-2.5">
                      {canApprove && (
                        <PrimaryButton
                          onClick={() => handleAction('approve')}
                          disabled={isUpdating}
                        >
                          <CheckIcon className="-ml-1 mr-1 h-3.5 w-3.5" />
                          Approve
                        </PrimaryButton>
                      )}
                      {canReject && (
                        <OutlineButton
                          onClick={() => handleAction('reject')}
                          disabled={isUpdating}
                          icon={<XMarkIcon className="h-3 w-3" />}
                        >
                          Reject
                        </OutlineButton>
                      )}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Approve / Reject comment dialog. Reuses the same modal as the bulk
          flow so single-drift and batch actions feel identical. Reject requires
          a comment for the audit trail; approve is optional. */}
      <BulkActionCommentModal
        isOpen={showCommentModal && actionType !== null && actionType !== 'status'}
        onClose={closeCommentModal}
        onConfirm={handleCommentConfirm}
        actionStatus={actionType === 'reject' ? DriftStatus.Rejected : DriftStatus.Approved}
        selectedCount={1}
        isProcessing={isUpdating}
        requireComment={actionType === 'reject'}
      />
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Drawer-local presentational atoms
   ──────────────────────────────────────────────────────────────────── */

/** SectionRule — double-rule visual break between drawer sections, mirroring
 *  the page-level posture surfaces. */
function SectionRule() {
  return (
    <div
      aria-hidden
      className="h-[4px]"
      style={{
        borderTop: '1px solid var(--color-rule, #E6E1D5)',
        borderBottom: '1px solid var(--color-rule, #E6E1D5)',
      }}
    />
  )
}

/** DrawerSectionHeader — eyebrow + italic-mixed headline + optional byline.
 *  Emphasis colour is passed in so each section can carry its own accent
 *  (sand-deep for the change, blue-deep for Guardian's read, etc.). */
function DrawerSectionHeader({
  eyebrow,
  headline,
  emColor,
  byline,
}: {
  eyebrow: string
  headline: React.ReactNode
  emColor: string
  byline?: string
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
        {eyebrow}
      </div>
      <h4
        className="mt-1.5 font-display text-[18px] font-extrabold leading-[1.22] tracking-[-0.014em]"
        style={{ color: 'var(--color-navy, #212938)' }}
      >
        <span
          style={{
            // Set the accent color the [&_em] selector picks up below
            ['--em-color' as string]: emColor,
          }}
          className="[&_em]:font-serif [&_em]:font-semibold [&_em]:italic [&_em]:[color:var(--em-color)]"
        >
          {headline}
        </span>
      </h4>
      {byline && (
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
          {byline}
        </div>
      )}
    </div>
  )
}

/** ValueBlock — "Previous" / "Now" code block. Thin coloured left rule + faint
 *  semantic tint carry meaning; the code itself stays navy on a near-paper bg
 *  so the change reads as content, not a warning toast. */
function ValueBlock({
  label,
  tone,
  value,
}: {
  label: string
  tone: 'red' | 'green'
  value: string
}) {
  const ruleColor =
    tone === 'red'
      ? 'var(--color-red-deep, #C84B4B)'
      : 'var(--color-green-deep, #4A9B6E)'
  const tintBg =
    tone === 'red' ? 'rgba(229,115,115,0.06)' : 'rgba(118,214,157,0.08)'
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        {label}
      </div>
      <div
        className="mt-1.5 max-h-32 overflow-auto rounded-[6px] border-l-[3px] px-3 py-2.5 text-[12.5px] leading-[1.5]"
        style={{
          borderLeftColor: ruleColor,
          background: tintBg,
          borderTop: '1px solid var(--color-rule-soft, #F0EBDD)',
          borderRight: '1px solid var(--color-rule-soft, #F0EBDD)',
          borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
        }}
      >
        <code
          className="block whitespace-pre-wrap break-words font-mono"
          style={{ color: 'var(--color-navy, #212938)' }}
        >
          {value}
        </code>
      </div>
    </div>
  )
}

/** RiskCapsule — 4-segment ladder (low / med / high / crit). Active tier fills
 *  with its semantic tone, lower tiers fill with a softer mute so the eye
 *  reads "score landed here" without resorting to a percentage gauge. */
function RiskCapsule({ score }: { score: number }) {
  const tier = riskTier(score)
  const pct = Math.round(score * 100)
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
          Risk score
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ color: RISK_TIER_COLOR[tier] }}
          >
            {RISK_TIER_LABEL[tier]}
          </span>
          <span
            className="font-mono text-[13px] font-bold tabular-nums"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            {pct}%
          </span>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1.5">
        {([0, 1, 2, 3] as RiskTier[]).map((t) => {
          const isLitOrBelow = t <= tier
          return (
            <div
              key={t}
              className="h-1.5 rounded-full"
              style={{
                background: isLitOrBelow
                  ? RISK_TIER_COLOR[t]
                  : 'var(--color-rule, #E6E1D5)',
                opacity: isLitOrBelow && t < tier ? 0.45 : 1,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

/** AuditRow — single timeline entry. Coloured icon disc on a thin vertical
 *  rule, navy event title, mute mono-tabular byline. Mirrors the queue
 *  table's severity treatment so the surface reads of a piece. */
function AuditRow({
  entry,
  isLast,
}: {
  entry: AuditEntry
  isLast: boolean
}) {
  const tone: AuditTone = AUDIT_TONE[entry.eventType] ?? 'neutral'
  const Icon =
    entry.eventType === 'Approved' || entry.eventType === 'AutoApproved' || entry.eventType === 'Remediated'
      ? CheckIcon
      : entry.eventType === 'Rejected'
        ? XMarkIcon
        : entry.eventType === 'ValuesEvolved'
          ? ExclamationTriangleIcon
          : InformationCircleIcon

  return (
    <li className="relative flex gap-3.5 pb-4">
      {!isLast && (
        <span
          aria-hidden
          className="absolute bottom-0 left-[11px] top-7 w-px"
          style={{ background: 'var(--color-rule, #E6E1D5)' }}
        />
      )}
      <span
        className="relative z-[1] flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border"
        style={{
          background: AUDIT_TONE_BG[tone],
          borderColor: AUDIT_TONE_FG[tone],
          color: AUDIT_TONE_FG[tone],
        }}
        aria-hidden
      >
        <Icon className="h-3 w-3" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p
            className="text-[13px] font-bold leading-tight"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            {AUDIT_LABEL[entry.eventType] ?? entry.eventType}
          </p>
          <p
            className="flex-shrink-0 font-mono text-[10px] font-semibold tabular-nums"
            style={{ color: 'var(--color-faint, #9CA3AF)' }}
          >
            {format(new Date(entry.eventTimestamp), 'MMM dd · HH:mm')}
          </p>
        </div>
        <p
          className="mt-0.5 text-[11px] font-semibold"
          style={{ color: 'var(--color-mute, #6B7280)' }}
        >
          {entry.triggeredByDisplayName || entry.triggeredBy || 'System'}
        </p>
        {entry.remediationSource && (
          <p
            className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: 'var(--color-faint, #9CA3AF)' }}
          >
            Source · {REMEDIATION_LABEL[entry.remediationSource] ?? entry.remediationSource}
          </p>
        )}
        {entry.comments && (
          <p
            className="mt-1.5 rounded-[6px] border-l-2 px-2.5 py-1.5 text-[12px] italic leading-[1.5]"
            style={{
              borderLeftColor: 'var(--color-rule, #E6E1D5)',
              background: 'var(--color-paper-warm, #FBF8F2)',
              color: 'var(--color-navy, #212938)',
            }}
          >
            &ldquo;{entry.comments}&rdquo;
          </p>
        )}
      </div>
    </li>
  )
}

/** ImpactBody — body slot of the resource-level impact NoticeRail. Spelled
 *  out so the editorial voice of the rail isn't clobbered by raw API copy. */
function ImpactBody({ approvalImpact }: { approvalImpact: ApprovalImpact }) {
  const impacted: ImpactedDrift[] = approvalImpact.impactedDrifts ?? []
  return (
    <span className="block">
      <span className="block">
        Approving this change triggers a resource-level update.
        {impacted.length > 0 && (
          <>
            {' '}
            {impacted.length} additional drift
            {impacted.length === 1 ? '' : 's'} will be included in the same
            commit.
          </>
        )}
      </span>
      {approvalImpact.reason && (
        <span
          className="mt-2 block"
          style={{ color: 'var(--color-mute, #6B7280)' }}
        >
          {approvalImpact.reason}
        </span>
      )}
      {impacted.length > 0 && (
        <span className="mt-3 block">
          <span
            className="block text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: 'var(--color-mute, #6B7280)' }}
          >
            Also landing
          </span>
          <span className="mt-1.5 block space-y-1">
            {impacted.map((d) => (
              <span key={d.id} className="flex items-center gap-2">
                <span
                  className="text-[12px] font-semibold"
                  style={{ color: 'var(--color-navy, #212938)' }}
                >
                  {d.setting}
                </span>
                <SevPill severity={d.severity} />
              </span>
            ))}
          </span>
        </span>
      )}
      {approvalImpact.recommendation && (
        <span
          className="mt-2 block italic"
          style={{ color: 'var(--color-mute, #6B7280)' }}
        >
          {approvalImpact.recommendation}
        </span>
      )}
    </span>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Loading skeleton — paper-warm bars on the editorial palette
   ──────────────────────────────────────────────────────────────────── */

function DrawerSkeleton() {
  return (
    <div className="space-y-7 animate-pulse">
      {/* Severity + date skeleton */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-4 w-32 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        </div>
        <div className="h-6 w-3/4 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="mt-2 h-3 w-1/2 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
      </div>

      {/* Configuration change skeleton */}
      <div>
        <div className="mb-3 h-3 w-44 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="space-y-3">
          <div className="h-20 rounded-[6px] bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-20 rounded-[6px] bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        </div>
      </div>

      {/* AI analysis skeleton */}
      <div>
        <div className="mb-3 h-3 w-36 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="mb-2 h-3 w-full rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="h-3 w-3/4 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
      </div>

      {/* Risk score skeleton */}
      <div>
        <div className="mb-2 h-3 w-24 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="grid grid-cols-4 gap-1.5">
          <div className="h-1.5 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-1.5 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-1.5 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-1.5 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        </div>
      </div>

      {/* Status dropdown skeleton */}
      <div>
        <div className="mb-2 h-3 w-14 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="h-10 w-full rounded-[8px] bg-[color:var(--color-rule-soft,#F0EBDD)]" />
      </div>

      {/* Action buttons skeleton */}
      <div className="flex gap-3">
        <div className="h-9 w-32 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        <div className="h-9 w-24 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
      </div>
    </div>
  )
}
